<template>
  <div class="page-container">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">{{ isReturnMode ? "退货出库" : "出库列表" }}</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <el-segmented v-if="!isReturnMode" v-model="activeTab" :options="tabOptions" @change="handleTabChange" block class="tabs-segment" />

      <div v-if="isReturnMode" class="tabs">
        <button
          class="tab"
          :class="{ active: returnTab === 'prepared' }"
          @click="switchReturnTab('prepared')"
        >
          待备料
        </button>
        <button
          class="tab"
          :class="{ active: returnTab === 'todo' }"
          @click="switchReturnTab('todo')"
        >
          已完成
        </button>
      </div>

      <div class="modal-search">
        <el-input
          v-model="supplierKeyword"
          class="search-input"
          placeholder="请输入供应商名称搜索"
          @input="handleSupplierSearch"
          @focus="supplierDropdown = true"
          clearable
          @clear="clearSelectedSupplier"
        />
        <div v-if="supplierDropdown" class="dropdown-menu">
          <div v-if="supplierLoading" class="dropdown-loading">加载中...</div>
          <div v-else-if="supplierList.length === 0" class="dropdown-empty">
            暂无数据
          </div>
          <div
            v-else
            v-for="s in supplierList"
            :key="s.id"
            class="dropdown-item"
            :class="{
              selected: selectedSupplier && selectedSupplier.id === s.id,
            }"
            @click="selectSupplier(s)"
          >
            {{ s.supplierName }}
          </div>
        </div>
      </div>

      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div v-loading="loading" element-loading-text="加载中..." class="list-loading-wrap">
        <div v-if="list.length === 0 && !loading" class="empty-state">
          <el-empty description="暂无数据" :image-size="100" />
        </div>

        <div v-else class="card-list">
        <div class="data-card" v-for="item in list" :key="item.id">
          <div v-if="isReturnMode" class="return-card">
            <div class="receive-card-header">
              <div class="receive-type-row">
                <div class="receive-type">退货出库</div>
                <div
                  class="receive-bad-tag"
                  :class="getReturnQuality(item).cls"
                >
                  {{ getReturnQuality(item).text }}
                </div>
              </div>
              <div class="receive-no">
                出库单号：{{ item.wareOutNo || getReturnCode(item) }}
              </div>
            </div>

            <div class="receive-card-body">
              <div class="return-body">
                <div class="return-body-left">
                  <div class="return-mat-title">物料</div>
                  <div class="return-mat-list">
                    <div
                      class="return-mat-item"
                      v-for="(no, idx) in getReturnMaterialNoLines(item)"
                      :key="`${item.id || ''}-${idx}`"
                    >
                      {{ no }}
                    </div>
                  </div>
                  <div class="return-mat-count">
                    物料种类：{{ getReturnMaterialCount(item) }}
                  </div>
                </div>
                <div class="return-body-right">
                  <div class="return-info-row">
                    <div class="return-info-label">供应商</div>
                    <div class="return-info-value">
                      {{ getSupplierName(item) || "-" }}
                    </div>
                  </div>
                  <div class="return-info-row">
                    <div class="return-info-label">出库总数</div>
                    <div class="return-info-value">
                      {{ formatQty(getReturnTotalQty(item)) }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="action-row">
                <el-button
                  v-if="returnTab === 'prepared'"
                  type="primary"
                  round
                  class="light-btn"
                  @click="openDetail(item)"
                >
                  进行备料
                </el-button>
              </div>
            </div>
          </div>

          <template v-else>
            <div class="receive-card-header">
              <div class="receive-type">送货出库</div>
              <div class="receive-no">
                出库单号：{{ item.wareOutNo || "-" }}
              </div>
            </div>

            <div class="receive-card-body">
              <div class="receive-grid">
                <div class="receive-field full">
                  <div class="receive-value">
                    供应商：{{ getSupplierName(item) || "-" }}
                  </div>
                </div>
                <div class="receive-field full">
                  <div class="receive-label">物料明细</div>
                  <div
                    v-if="outDetailRows(item).length"
                    class="material-table"
                  >
                    <div class="material-table-header">
                      <div class="material-col code">物料编码</div>
                      <div class="material-col desc">物料描述</div>
                      <div class="material-col qty">出库数量</div>
                    </div>
                    <div
                      v-for="(r, idx) in outDetailRows(item)"
                      :key="(r.id || r.materialNo || '') + '-' + idx"
                      class="material-table-row"
                    >
                      <div class="material-col code">
                        {{ r.materialNo || "-" }}
                      </div>
                      <div
                        class="material-col desc"
                        :title="r.materialDesc || ''"
                      >
                        {{ r.materialDesc || "-" }}
                      </div>
                      <div class="material-col qty">
                        {{ formatQty(r.wareOutNum) }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="receive-value">-</div>
                </div>
              </div>

              <div v-if="activeTab === 'todo'" class="action-row">
                <el-button
                  type="primary"
                  round
                  class="light-btn"
                  @click="openConfirmDialog(item)"
                >
                  确认出库
                </el-button>
              </div>
            </div>
          </template>
        </div>

        <div class="list-footer">
          <div class="page-info">已加载 {{ list.length }} / {{ total }}</div>
          <el-button
            type="primary"
            text
            @click="loadMore"
            :loading="loadingMore"
            :disabled="finished"
          >
            {{ finished ? "没有更多了" : loadingMore ? "加载中..." : "加载更多" }}
          </el-button>
        </div>
      </div>
      </div>
    </main>

    <!-- 确认出库弹窗 -->
    <el-dialog v-model="confirmDialogVisible" title="确认出库" width="85%" class="confirm-dialog">
      <div class="confirm-section">
        <div class="confirm-row">
          <div class="confirm-label">送货单号</div>
          <div class="confirm-value">{{ confirmTarget?.wareOutNo || "-" }}</div>
        </div>
        <div class="confirm-row">
          <div class="confirm-label">供应商</div>
          <div class="confirm-value">{{ getSupplierName(confirmTarget) || "-" }}</div>
        </div>
      </div>
      <div class="confirm-divider"></div>
      <div v-if="confirmMaterialRows.length" class="confirm-materials">
        <div class="confirm-material-header">
          <span class="cm-col">物料编码</span>
          <span class="cm-col cm-qty">数量</span>
        </div>
        <div
          v-for="(m, idx) in confirmMaterialRows"
          :key="idx"
          class="confirm-material-row"
        >
          <span class="cm-col cm-code">{{ m.materialNo || "-" }}</span>
          <span class="cm-col cm-qty">{{ formatQty(m.wareOutNum) }}</span>
        </div>
      </div>
      <div class="confirm-msg">是否确认出库？</div>
      <template #footer>
        <el-button @click="closeConfirmDialog" round :disabled="confirmSubmitting">取消</el-button>
        <el-button type="primary" round class="light-btn" @click="submitConfirmOut" :disabled="confirmSubmitting" :loading="confirmSubmitting">
          {{ confirmSubmitting ? "提交中..." : "确认出库" }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 退货出库详情弹窗 -->
    <el-dialog v-model="returnDetailVisible" title="退货出库详情" width="90%" class="detail-dialog">
      <div v-if="returnDetailLoading" v-loading="returnDetailLoading" element-loading-text="加载中..." style="min-height: 100px;"></div>
      <div v-else-if="returnDetailError" class="confirm-msg error-msg">{{ returnDetailError }}</div>
      <template v-else>
        <div class="confirm-row">
          <div class="confirm-label">出库单号</div>
          <div class="confirm-value">{{ returnDetail?.wareOutNo || "-" }}</div>
        </div>
        <div class="confirm-row">
          <div class="confirm-label">供应商</div>
          <div class="confirm-value">{{ returnDetailSupplierName || "-" }}</div>
        </div>
        <div class="confirm-divider"></div>

        <div v-if="returnDetailMaterials.length" class="return-material-list">
          <div
            class="return-material-card"
            v-for="(m, mIdx) in returnDetailMaterials"
            :key="m.id || (m.materialNo || '') + '-' + mIdx"
          >
            <div class="return-material-header">
              <div class="return-material-index">{{ mIdx + 1 }}</div>
              <div class="return-material-main">
                <div class="return-material-no">{{ m.materialNo || "-" }}</div>
                <div class="return-material-subrow">
                  <div class="return-material-desc">{{ m.materialDesc || "-" }}</div>
                  <div class="return-material-badges">
                    <el-tag v-if="m.wareOutNum != null" size="small" type="primary" round>需出库：{{ formatQty(m.wareOutNum) }}</el-tag>
                    <el-tag v-if="m.prepareNum != null" size="small" round>待备料：{{ formatQty(m.prepareNum) }}</el-tag>
                    <el-tag v-if="m.demandNum != null" size="small" type="info" round>需求：{{ formatQty(m.demandNum) }}</el-tag>
                  </div>
                </div>
              </div>
            </div>

            <div class="return-material-toggle" v-if="Array.isArray(m.stockList) && m.stockList.length">
              <el-button type="primary" text size="small" @click="toggleReturnMaterial(m)">
                {{ m._collapsed ? "展开" : "收起" }}
              </el-button>
            </div>

            <div class="stock-table" v-if="Array.isArray(m.stockList) && m.stockList.length && !m._collapsed">
              <div class="stock-head">
                <div class="stock-col">库位</div>
                <div class="stock-col">托盘码</div>
                <div class="stock-col">数量</div>
              </div>
              <div class="stock-row" v-for="(s, sIdx) in m.stockList" :key="(s.id || s.palletCode || '') + '-' + sIdx">
                <div class="stock-cell">
                  <div class="stock-main">{{ s.wareName || "-" }}</div>
                </div>
                <div class="stock-cell">
                  <div class="stock-main">{{ s.palletCode || "-" }}</div>
                </div>
                <div class="stock-cell">
                  <el-input
                    class="qty-input"
                    type="number"
                    v-model="s._prepareQty"
                    placeholder="请输入备料数量"
                    inputmode="decimal"
                    @blur="handleReturnQtyBlur(m, s)"
                    size="small"
                  />
                  <div class="qty-hint">可用库存：{{ formatQty(s.stockNum) }}</div>
                </div>
              </div>
            </div>
            <div v-else-if="!Array.isArray(m.stockList) || m.stockList.length === 0" class="confirm-value">
              暂无库存明细
            </div>
          </div>
        </div>
        <div v-else class="confirm-value">暂无明细</div>
      </template>
      <template #footer>
        <el-button @click="closeReturnDetail" round>关闭</el-button>
        <el-button type="primary" round class="light-btn" @click="confirmReturnWareOut" :disabled="returnSubmitLoading" :loading="returnSubmitLoading">
          {{ returnSubmitLoading ? "出库中..." : "出库" }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ElMessageBox } from "element-plus";
import toast from "@/utils/toast";
import {
  getWareOutPage,
  confirmWareOut,
  getReturnWareOutPage,
  getWareOutDetailHf,
  immediateWareOutHf,
} from "@/api/wareOut";
import { getSupplierPageGtZero } from "@/api/wareInReceiveCreate";

export default {
  name: "WareOutList",
  setup() {
    const router = useRouter();
    const route = useRoute();

    const isReturnMode = computed(() => {
      const val = route.query?.return;
      return val === "1" || val === 1 || val === true || val === "true";
    });

    const activeTab = ref("todo");
    const tabOptions = [
      { label: "待出库", value: "todo" },
      { label: "已完成", value: "done" },
    ];
    const handleTabChange = (val) => {
      switchTab(val);
    };
    const returnTab = ref("prepared");
    const current = ref(1);
    const size = ref(10);

    const list = ref([]);
    const total = ref(0);

    const loading = ref(false);
    const loadingMore = ref(false);

    const statusMsg = ref("");
    const statusType = ref("info");

    const supplierKeyword = ref("");
    const supplierLoading = ref(false);
    const supplierDropdown = ref(false);
    const supplierList = ref([]);
    const selectedSupplier = ref(null);
    const supplierTimer = ref(0);

    const confirmDialogVisible = ref(false);
    const confirmSubmitting = ref(false);
    const confirmTarget = ref(null);

    const confirmMaterialRows = computed(() => {
      if (!confirmTarget.value) return [];
      const list = confirmTarget.value?.outDetailList || confirmTarget.value?.wareOutDetailList || [];
      return Array.isArray(list) ? list : [];
    });

    const wareOutStatus = computed(() => {
      if (activeTab.value === "todo") return 1;
      return 2;
    });

    const returnWareOutStatus = computed(() => {
      return returnTab.value === "prepared" ? 0 : 3;
    });

    const finished = computed(
      () => list.value.length >= total.value && total.value > 0,
    );

    const handleBack = () => router.push("/home");
    const goHome = () => router.push("/home");

    const formatQty = (val) => {
      if (val === null || val === undefined || val === "") return "-";
      const n = Number(val);
      if (Number.isNaN(n)) return String(val);
      // 最高保留 4 位小数，去掉多余尾零
      return parseFloat(n.toFixed(4)).toString();
    };

    // 退货数量按 4 位小数对齐，避免累加浮点误差导致校验失败
    const roundQty = (val) => {
      const n = Number(val);
      if (!Number.isFinite(n)) return 0;
      return Math.round(n * 10000) / 10000;
    };

    const formatQtyWithUnit = (val, unit) => {
      if (val === null || val === undefined || val === "") return "-";
      const qty = typeof val === "string" ? val : formatQty(val);
      if (!qty || qty === "-") return "-";
      return `${qty}${unit || ""}`;
    };

    const outDetailRows = (item) => {
      const arr = item?.outDetailList || item?.wareOutDetailList || [];
      const list = Array.isArray(arr) ? arr : [];
      return list.map((d) => ({
        id: d?.id,
        materialNo: d?.materialNo,
        materialDesc: d?.materialDesc,
        wareOutNum: d?.wareOutNum,
      }));
    };

    const getSupplierName = (item) => {
      const v = item || {};
      if (v?.supplierName) return v.supplierName;
      if (v?.supplier?.supplierName) return v.supplier.supplierName;
      if (v?.supplierDto?.supplierName) return v.supplierDto.supplierName;

      const detailObj = v?.detail;
      if (detailObj && typeof detailObj === "object") {
        if (detailObj?.supplierName) return detailObj.supplierName;
        if (detailObj?.supplier?.supplierName)
          return detailObj.supplier.supplierName;
      }
      if (typeof detailObj === "string") {
        try {
          const parsed = JSON.parse(detailObj);
          if (parsed?.supplierName) return parsed.supplierName;
          if (parsed?.supplier?.supplierName)
            return parsed.supplier.supplierName;
        } catch (_) {
          // ignore invalid json detail string
        }
      }

      const detailLists = [
        v?.outDetailList,
        v?.wareOutDetailList,
        v?.detailList,
        v?.details,
      ];
      for (let i = 0; i < detailLists.length; i += 1) {
        const arr = Array.isArray(detailLists[i]) ? detailLists[i] : [];
        for (let j = 0; j < arr.length; j += 1) {
          const d = arr[j] || {};
          if (d?.supplierName) return d.supplierName;
          if (d?.supplier?.supplierName) return d.supplier.supplierName;
        }
      }
      return "";
    };

    const getReturnTotalQty = (item) => {
      const details = Array.isArray(item?.outDetailList)
        ? item.outDetailList
        : [];
      let sum = 0;
      for (let i = 0; i < details.length; i += 1) {
        const raw =
          details[i]?.wareOutNum ??
          details[i]?.demandNum ??
          details[i]?.prepareNum;
        const n = Number(raw);
        if (Number.isFinite(n)) sum += n;
      }
      return sum;
    };

    const getReturnMaterialCount = (item) => {
      const details = Array.isArray(item?.outDetailList)
        ? item.outDetailList
        : [];
      return details.length || 0;
    };

    const getReturnMaterialSummary = (item) => {
      const details = Array.isArray(item?.outDetailList)
        ? item.outDetailList
        : [];
      if (details.length === 0) return "-";
      const first = details[0];
      const no = first?.materialNo || "-";
      const desc = first?.materialDesc || "-";
      const more = details.length > 1 ? ` 等${details.length}项` : "";
      return `${no} ${desc}${more}`;
    };

    const getReturnFirstMaterial = (item) => {
      const details = Array.isArray(item?.outDetailList)
        ? item.outDetailList
        : [];
      if (details.length === 0) return { no: "-", desc: "-" };
      const first = details[0] || {};
      const no = first?.materialNo || "-";
      const desc = first?.materialDesc || "-";
      return { no, desc };
    };

    const getReturnMaterialNoLines = (item) => {
      const details = Array.isArray(item?.outDetailList)
        ? item.outDetailList
        : [];
      const nos = details
        .map((d) => (d?.materialNo ? String(d.materialNo) : ""))
        .filter((x) => x);

      if (nos.length === 0) return ["-"];
      if (nos.length <= 5) return nos;
      return nos.slice(0, 5).concat(["..."]);
    };

    const getReturnQuality = (item) => {
      const flag = item?.isBad;
      if (flag === 1 || flag === "1") return { text: "不良品", cls: "bad" };
      if (flag === 0 || flag === "0") return { text: "良品", cls: "good" };

      const details = Array.isArray(item?.outDetailList)
        ? item.outDetailList
        : [];
      for (let i = 0; i < details.length; i += 1) {
        const stocks = Array.isArray(details[i]?.stockList)
          ? details[i].stockList
          : [];
        for (let j = 0; j < stocks.length; j += 1) {
          if (stocks[j]?.isBad === 1 || stocks[j]?.isBad === "1") {
            return { text: "不良品", cls: "bad" };
          }
        }
      }
      return { text: "良品", cls: "good" };
    };

    const getReturnCode = (item) => {
      const code = item?.plsNo || item?.resourceNo || item?.transferNo;
      if (code) return String(code);
      const id = item?.id;
      if (!id) return "-";
      const s = String(id);
      return s.length > 8 ? s.slice(-8) : s;
    };

    const handleSupplierSearch = () => {
      supplierDropdown.value = true;
      if (supplierTimer.value) window.clearTimeout(supplierTimer.value);
      supplierTimer.value = window.setTimeout(async () => {
        await fetchSuppliers();
      }, 250);
    };

    const fetchSuppliers = async () => {
      if (supplierLoading.value) return;
      supplierLoading.value = true;
      try {
        const res = await getSupplierPageGtZero({
          supplierName: supplierKeyword.value.trim(),
          current: 1,
          size: 20,
        });
        supplierList.value = res?.data?.records || [];
      } catch (e) {
        supplierList.value = [];
        toast.error(e?.message || "供应商加载失败");
      } finally {
        supplierLoading.value = false;
      }
    };

    const selectSupplier = async (s) => {
      selectedSupplier.value = s;
      supplierDropdown.value = false;
      supplierKeyword.value = s?.supplierName || "";
      await fetchPage({ reset: true });
    };

    const clearSelectedSupplier = async () => {
      selectedSupplier.value = null;
      supplierKeyword.value = "";
      supplierList.value = [];
      supplierDropdown.value = false;
      await fetchPage({ reset: true });
    };

    const fetchPage = async ({ reset } = { reset: false }) => {
      if (reset) {
        current.value = 1;
        list.value = [];
        total.value = 0;
      }

      loading.value = reset;
      loadingMore.value = !reset;
      statusMsg.value = "";

      try {
        console.log(
          "fetchPage called, isReturnMode:",
          isReturnMode.value,
          "returnWareOutStatus:",
          returnWareOutStatus.value,
        );
        const res = isReturnMode.value
          ? await getReturnWareOutPage({
              current: current.value,
              size: size.value,
              supplierId: selectedSupplier.value?.id,
              wareOutStatus: returnWareOutStatus.value,
            })
          : await getWareOutPage({
              current: current.value,
              size: size.value,
              wareOutStatus: wareOutStatus.value,
              supplierId: selectedSupplier.value?.id,
              wareOutType: 0,
            });

        const records = res?.data?.records || [];
        const nextList = reset ? records : list.value.concat(records);

        list.value = nextList;
        total.value = res?.data?.total || nextList.length;
      } catch (e) {
        statusMsg.value = e?.message || "加载失败";
        statusType.value = "error";
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    const switchTab = async (tab) => {
      activeTab.value = tab;
      await fetchPage({ reset: true });
    };

    const switchReturnTab = async (tab) => {
      console.log(
        "switchReturnTab called with:",
        tab,
        "current returnTab:",
        returnTab.value,
      );
      if (returnTab.value === tab) return;
      if (!isReturnMode.value) return;
      returnTab.value = tab;
      console.log(
        "returnTab updated to:",
        returnTab.value,
        "returnWareOutStatus:",
        returnWareOutStatus.value,
      );
      await fetchPage({ reset: true });
    };

    const loadMore = async () => {
      if (finished.value || loading.value || loadingMore.value) return;
      current.value += 1;
      await fetchPage({ reset: false });
    };

    const closeConfirmDialog = () => {
      confirmDialogVisible.value = false;
      confirmTarget.value = null;
      confirmSubmitting.value = false;
    };

    const openConfirmDialog = (item) => {
      const id = item?.id;
      if (!id) {
        toast.error("缺少出库单ID");
        return;
      }
      confirmTarget.value = item;
      confirmDialogVisible.value = true;
    };

    const submitConfirmOut = async () => {
      if (confirmSubmitting.value) return;
      const id = confirmTarget.value?.id;
      if (!id) {
        toast.error("缺少出库单ID");
        return;
      }

      confirmSubmitting.value = true;
      try {
        // 根据模式区分确认出库参数
        const target = confirmTarget.value;
        const list = target?.wareOutDetailList || target?.outDetailList || [];
        if (!Array.isArray(list) || list.length === 0) {
          throw new Error("缺少出库明细");
        }

        const payload = {
          id,
          wareOutType: isReturnMode.value ? 1 : 0, // 0=送货出库, 1=退货出库
          wareOutDetailList: list.map((d) => {
            const raw = d?.wareOutNum ?? d?.prepareNum ?? d?.demandNum;
            const n = Number(raw);
            const next =
              Number.isFinite(n) && n > 0 ? String(n) : String(raw ?? "");
            return {
              ...d,
              actualWareOutNum: next,
              wareOutNum: next,
            };
          }),
        };
        const res = await confirmWareOut(payload);

        if (res?.success === false) throw new Error(res?.msg || "确认出库失败");

        toast.success("确认出库成功");
        closeConfirmDialog();
        await fetchPage({ reset: true });
      } catch (e) {
        toast.error(e?.message || "确认出库失败");
      } finally {
        confirmSubmitting.value = false;
      }
    };

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      fetchPage({ reset: true });
    });

    const returnDetailVisible = ref(false);
    const returnDetailLoading = ref(false);
    const returnDetailError = ref("");
    const returnDetail = ref(null);
    const returnDetailIsBad = ref(null);

    const returnDetailMaterials = ref([]);
    const returnSubmitLoading = ref(false);

    const returnDetailSupplierName = computed(() => {
      const d = returnDetail.value || {};
      const detailSupplier = getSupplierName(d);
      if (detailSupplier) return detailSupplier;
      const ms = Array.isArray(returnDetailMaterials.value)
        ? returnDetailMaterials.value
        : [];
      for (let i = 0; i < ms.length; i += 1) {
        const name = ms[i]?.supplierName;
        if (name) return name;
      }
      return "";
    });

    const normalizeReturnDetailMaterials = (detail) => {
      const d = detail || {};
      const arr = d?.outDetailList || d?.wareOutDetailList || [];
      const list = Array.isArray(arr) ? arr : [];
      return list.map((x) => ({
        id: x?.id,
        supplierName: x?.supplierName,
        materialId: x?.materialId,
        materialNo: x?.materialNo,
        materialDesc: x?.materialDesc,
        demandNum: x?.demandNum,
        prepareNum: x?.prepareNum,
        wareOutNum: x?.wareOutNum,
        _collapsed: true,
        stockList: (Array.isArray(x?.stockList) ? x.stockList : []).map(
          (s) => ({
            ...s,
            _prepareQty: "",
          }),
        ),
      }));
    };

    const toggleReturnMaterial = (m) => {
      if (!m) return;
      m._collapsed = !m._collapsed;
    };

    const handleReturnQtyBlur = (m, s) => {
      const raw = s?._prepareQty;
      if (raw === null || raw === undefined || raw === "") {
        s._prepareQty = "";
        return;
      }
      const n = roundQty(raw);
      if (!Number.isFinite(n) || n < 0) {
        s._prepareQty = "";
        return;
      }
      const available = roundQty(s?.stockNum);
      if (Number.isFinite(available) && n > available) {
        s._prepareQty = String(available);
        return;
      }
      s._prepareQty = String(n);
    };

    const buildReturnWareOutPayload = () => {
      const id = returnDetail.value?.id;
      if (!id) throw new Error("缺少出库单ID");

      const wareOutDetailList = [];
      const ms = Array.isArray(returnDetailMaterials.value)
        ? returnDetailMaterials.value
        : [];
      for (let i = 0; i < ms.length; i += 1) {
        const m = ms[i];
        const stocks = Array.isArray(m?.stockList) ? m.stockList : [];
        const pickedStocks = [];
        let materialTotal = 0;
        for (let j = 0; j < stocks.length; j += 1) {
          const s = stocks[j];
          const qty = roundQty(s?._prepareQty);
          if (!Number.isFinite(qty) || qty <= 0) continue;
          materialTotal = roundQty(materialTotal + qty);
          // 不传批次：后端按先进先出自动分配
          pickedStocks.push({
            palletId: s?.palletId,
            wareId: s?.wareId,
            wareOutNum: String(qty),
          });
        }
        if (pickedStocks.length > 0) {
          const materialId = m?.materialId;
          if (!materialId) {
            throw new Error(
              `物料 ${m?.materialNo || ""} 缺少materialId，无法出库`,
            );
          }
          wareOutDetailList.push({
            materialId,
            wareOutNum: materialTotal,
            stockList: pickedStocks,
          });
        }
      }

      return {
        id,
        wareOutDetailList,
      };
    };

    const getValidatedReturnWareOutPayload = () => {
      const payload = buildReturnWareOutPayload();
      if (
        !Array.isArray(payload.wareOutDetailList) ||
        payload.wareOutDetailList.length === 0
      ) {
        toast.error("请先输入备料数量");
        return null;
      }

      const ms = Array.isArray(returnDetailMaterials.value)
        ? returnDetailMaterials.value
        : [];
      for (let i = 0; i < ms.length; i += 1) {
        const m = ms[i];
        const requiredQty = roundQty(m?.wareOutNum || 0);
        const stocks = Array.isArray(m?.stockList) ? m.stockList : [];

        let actualTotal = 0;
        for (let j = 0; j < stocks.length; j += 1) {
          const qty = roundQty(stocks[j]?._prepareQty || 0);
          if (Number.isFinite(qty) && qty > 0) {
            actualTotal = roundQty(actualTotal + qty);
          }
        }

        if (actualTotal !== requiredQty) {
          toast.error(
            `物料 ${m.materialNo || ""} 备料数量与需出库数量不相等`,
          );
          return null;
        }
      }

      return payload;
    };

    const confirmReturnWareOut = async () => {
      if (returnSubmitLoading.value) return;

      let payload;
      try {
        payload = getValidatedReturnWareOutPayload();
      } catch (e) {
        toast.error(e?.message || "提交失败");
        return;
      }
      if (!payload) return;

      try {
        await ElMessageBox.confirm("是否确认出库？", "确认出库", {
          confirmButtonText: "确认出库",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch {
        return;
      }

      await submitReturnWareOut(payload);
    };

    const submitReturnWareOut = async (payload) => {
      try {
        returnSubmitLoading.value = true;
        const res = await immediateWareOutHf(payload);
        if (res?.success === false) throw new Error(res?.msg || "出库失败");
        toast.success("出库成功");
        closeReturnDetail();
        await fetchPage({ reset: true });
      } catch (e) {
        toast.error(e?.message || "提交失败");
      } finally {
        returnSubmitLoading.value = false;
      }
    };

    const closeReturnDetail = () => {
      returnDetailVisible.value = false;
      returnDetailLoading.value = false;
      returnDetailError.value = "";
      returnDetail.value = null;
      returnDetailIsBad.value = null;
      returnDetailMaterials.value = [];
      returnSubmitLoading.value = false;
    };

    const openDetail = async (item) => {
      const id = item?.id;
      if (!id) return;
      returnDetailVisible.value = true;
      returnDetailLoading.value = true;
      returnDetailError.value = "";
      returnDetail.value = null;
      const quality = getReturnQuality(item);
      const isBad = quality?.cls === "bad" ? 1 : 0;
      returnDetailIsBad.value = isBad;
      try {
        const res = await getWareOutDetailHf(id, isBad);
        returnDetail.value = res?.data || null;
        returnDetailMaterials.value = normalizeReturnDetailMaterials(
          returnDetail.value,
        );
      } catch (e) {
        returnDetailError.value = e?.message || "加载失败";
      } finally {
        returnDetailLoading.value = false;
      }
    };

    return {
      isReturnMode,
      activeTab,
      tabOptions,
      handleTabChange,
      list,
      total,
      loading,
      loadingMore,
      finished,
      statusMsg,
      statusType,
      supplierKeyword,
      supplierLoading,
      supplierDropdown,
      supplierList,
      selectedSupplier,
      handleSupplierSearch,
      selectSupplier,
      clearSelectedSupplier,
      handleBack,
      goHome,
      switchTab,
      returnTab,
      switchReturnTab,
      returnWareOutStatus,
      loadMore,
      formatQty,
      formatQtyWithUnit,
      getSupplierName,
      outDetailRows,
      getReturnQuality,
      getReturnCode,
      getReturnTotalQty,
      getReturnMaterialCount,
      getReturnMaterialSummary,
      getReturnFirstMaterial,
      getReturnMaterialNoLines,

      openConfirmDialog,
      closeConfirmDialog,
      submitConfirmOut,
      confirmDialogVisible,
      confirmTarget,
      confirmMaterialRows,
      confirmSubmitting,

      openDetail,
      returnDetailVisible,
      closeReturnDetail,
      returnDetailLoading,
      returnDetailError,
      returnDetail,
      returnDetailSupplierName,
      returnDetailMaterials,
      toggleReturnMaterial,
      handleReturnQtyBlur,
      returnSubmitLoading,
      confirmReturnWareOut,
      submitReturnWareOut,
    };
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  padding-bottom: 18px;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  border: none;
  background: transparent;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.tabs-segment {
  margin-bottom: 10px;
}

.tabs-segment :deep(.el-segmented) {
  border-radius: 12px;
  padding: 3px;
}

.tabs-segment :deep(.el-segmented__item) {
  border-radius: 10px;
  padding: 8px 0;
  margin: 0;
}

.tabs {
  display: flex;
  background: var(--card-background);
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
  margin-bottom: 10px;
}

.tab {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 0;
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
}

.tab.active {
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  font-weight: 600;
}

.modal-search {
  position: relative;
  margin-bottom: 8px;
}

.search-input :deep(.el-input__wrapper) {
  height: 38px;
  border-radius: 8px;
}

.filter-hint {
  margin-top: 4px;
  font-size: 11px;
  color: var(--text-secondary);
}

.dropdown-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  border-radius: 8px;
  box-shadow: 0 6px 16px var(--shadow-light);
  border: 1px solid var(--border-color);
  background: var(--card-background);
  overflow: hidden;
  max-height: 240px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 50;
}

.dropdown-loading,
.dropdown-empty {
  padding: 10px;
  font-size: 12px;
  color: var(--text-secondary);
}

.dropdown-item {
  padding: 10px 12px;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
}

.dropdown-item + .dropdown-item {
  border-top: 1px solid var(--border-color);
}

.dropdown-item:hover {
  background: var(--background-color);
}

.dropdown-item.selected {
  background: rgba(35, 177, 192, 0.12);
  font-weight: 600;
}

.status-banner {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
}

.status-banner.info {
  background: rgba(29, 78, 216, 0.1);
  color: #1d4ed8;
}

.status-banner.error {
  background: rgba(185, 28, 28, 0.1);
  color: #b91c1c;
}

.empty-state {
  margin-top: 20px;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.data-card {
  background: var(--card-background);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--shadow-light);
  border: 1px solid var(--border-color);
}

.receive-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: var(--background-color);
  border-bottom: 1px solid var(--border-color);
}

.receive-type {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(35, 177, 192, 0.14);
  color: var(--primary-color);
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
}

.receive-type-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.receive-bad-tag {
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.receive-bad-tag.bad {
  background: rgba(239, 68, 68, 0.16);
  color: #ef4444;
}

.receive-bad-tag.good {
  background: rgba(16, 185, 129, 0.14);
  color: #10b981;
}

.receive-no {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
}

.receive-card-body {
  padding: 8px 10px;
}

.receive-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.receive-field {
  background: var(--background-color);
  border-radius: 6px;
  padding: 6px 8px;
}

.receive-field.full {
  grid-column: 1 / -1;
}

.receive-label {
  font-size: 11px;
  color: var(--text-secondary);
  margin-bottom: 3px;
}

.receive-value {
  color: var(--text-primary);
  font-size: 12px;
  word-break: break-all;
  line-height: 1.3;
}

.material-table {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.material-table-header,
.material-table-row {
  display: grid;
  grid-template-columns: 1fr 1.4fr 0.8fr;
  gap: 8px;
  align-items: center;
}

.material-table-header {
  font-size: 11px;
  color: var(--text-secondary);
}

.material-table-row {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 6px 8px;
}

.material-col {
  min-width: 0;
  font-size: 12px;
  line-height: 1.2;
}

.material-col.code {
  font-weight: 700;
  color: var(--primary-color);
}

.material-col.desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-primary);
}

.material-col.qty {
  text-align: right;
  font-weight: 700;
  color: var(--text-primary);
}

.return-card .receive-card-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-color);
}

.return-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: stretch;
}

.return-body-left {
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.return-mat-title {
  font-size: 11px;
  color: var(--text-secondary);
}

.return-mat-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.return-mat-item {
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1.2;
  word-break: break-all;
}

.return-mat-count {
  font-size: 11px;
  color: var(--text-secondary);
}

.return-body-right {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
}

.return-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
}

.return-info-label {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.return-info-value {
  color: var(--text-primary);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.action-row {
  margin-top: 8px;
}

.light-btn.el-button--primary {
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  color: var(--text-secondary);
  font-size: 12px;
}

.confirm-dialog :deep(.el-dialog) {
  border-radius: 12px;
  max-width: 320px;
  margin: 0 auto;
}

.confirm-dialog :deep(.el-dialog__header) {
  padding: 12px 16px;
  margin: 0;
  border-bottom: 1px solid var(--border-color);
  text-align: center;
}

.confirm-dialog :deep(.el-dialog__title) {
  font-size: 15px;
  font-weight: 600;
}

.confirm-dialog :deep(.el-dialog__body) {
  padding: 16px;
}

.confirm-dialog :deep(.el-dialog__footer) {
  padding: 12px 16px 16px;
  display: flex;
  gap: 10px;
}

.confirm-dialog :deep(.el-dialog__footer .el-button) {
  flex: 1;
}

.list-loading-wrap {
  min-height: 100px;
}

.confirm-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.confirm-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.confirm-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.confirm-value {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}

.confirm-divider {
  height: 1px;
  background: var(--border-color);
  margin: 8px 0;
}

.confirm-msg {
  font-size: 14px;
  color: var(--text-primary);
  text-align: center;
}

.confirm-materials {
  margin-bottom: 10px;
}

.confirm-material-header {
  display: flex;
  padding: 6px 8px;
  background: var(--background-color);
  border-radius: 6px 6px 0 0;
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 600;
}

.confirm-material-row {
  display: flex;
  padding: 6px 8px;
  font-size: 12px;
  border-bottom: 1px solid var(--border-color);
}

.confirm-material-row:last-child {
  border-bottom: none;
  border-radius: 0 0 6px 6px;
}

.cm-col {
  flex: 1;
}

.cm-code {
  font-weight: 600;
  color: var(--text-primary);
}

.cm-qty {
  text-align: right;
  color: var(--primary-color);
  font-weight: 600;
}

.error-msg {
  color: #ef4444;
}

.detail-dialog :deep(.el-dialog) {
  border-radius: 12px;
}

.detail-dialog :deep(.el-dialog__header) {
  padding: 12px 16px;
  margin: 0;
  border-bottom: 1px solid var(--border-color);
}

.return-material-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.return-material-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px;
}

.return-material-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.return-material-index {
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.return-material-main {
  flex: 1;
  min-width: 0;
}

.return-material-no {
  font-size: 13px;
  font-weight: 700;
  color: var(--primary-color);
}

.return-material-subrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 3px;
}

.return-material-desc {
  flex: 1;
  min-width: 0;
  color: var(--text-secondary);
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.return-material-badges {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.return-material-toggle {
  padding: 0 0 4px 28px;
}

.stock-table {
  margin-top: 6px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;
}

.stock-head {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
  background: var(--background-color);
  padding: 6px 8px;
  font-size: 11px;
  color: var(--text-secondary);
}

.stock-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
  padding: 6px 8px;
  border-top: 1px solid var(--border-color);
  font-size: 12px;
  color: var(--text-primary);
}

.stock-cell {
  min-width: 0;
}

.qty-input :deep(.el-input__wrapper) {
  height: 30px;
  border-radius: 6px;
}

.qty-hint {
  font-size: 10px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.stock-main {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
